import React, { useEffect, useState } from 'react';
import { Camera, Download, Target, Webcam } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';
import '@/styles/aimup-redesign-announcement.css';

// Einmalige Release-Ankündigung je Nutzer und Browser; für spätere Ankündigungen eigene Kennung verwenden.
export const BOARD_MANAGER_ANNOUNCEMENT_ID = 'board-manager-store-launch-106-2026-10';
export const boardManagerAnnouncementKey = (userId: string) =>
  `aimup-announcement:${BOARD_MANAGER_ANNOUNCEMENT_ID}:${userId}`;

interface BoardManagerAnnouncementProps {
  userId: string;
  onVisibilityChange: (visible: boolean) => void;
}

export const BoardManagerAnnouncement: React.FC<BoardManagerAnnouncementProps> = ({
  userId,
  onVisibilityChange,
}) => {
  const navigate = useLocalizedNavigate();
  const [open, setOpen] = useState(false);
  const titleRef = React.useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    let show = true;
    try {
      show = window.localStorage.getItem(boardManagerAnnouncementKey(userId)) !== 'seen';
    } catch {
      // Bei gesperrtem Browser-Speicher die Ankündigung trotzdem anzeigen.
    }
    setOpen(show);
    onVisibilityChange(show);
  }, [userId, onVisibilityChange]);

  const dismiss = () => {
    try {
      window.localStorage.setItem(boardManagerAnnouncementKey(userId), 'seen');
    } catch {
      // Die Ankündigung soll auch ohne Speicher geschlossen werden können.
    }
    setOpen(false);
    onVisibilityChange(false);
  };

  const goToDownload = () => {
    dismiss();
    navigate('/board-manager');
  };

  // Optik nach den Redesign-Dialogen (aur-pt-dlg: Karte #182335, Rand #263449, Radius 18, Titel 18–20 px/800,
  // Text 13 px #aeb6c3, Hauptknopf #1db943) und der Board-Manager-Seite (Icon-Kachel); Verhalten unverändert.
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) dismiss(); }}>
      <DialogContent
        onOpenAutoFocus={(event) => {
          // Den Titel statt des Download-Buttons fokussieren: kein grüner Rahmen beim Öffnen.
          // Andere Dialog-Bedienelemente behalten ihre sichtbare Fokusmarkierung.
          event.preventDefault();
          titleRef.current?.focus();
        }}
        className="aur-bma max-w-[440px] gap-0 rounded-[18px] border-[#263449] bg-[#182335] p-[22px] text-[#f4f6f8] shadow-none max-sm:w-[calc(100%-28px)] max-sm:p-5 sm:rounded-[18px]">
        <DialogHeader className="aur-bma-head space-y-0 text-left sm:text-left">
          <span className="aur-bma-icon" aria-hidden="true"><Webcam size={22} /></span>
          <p className="aur-bma-eyebrow">Jetzt verfügbar</p>
          <DialogTitle ref={titleRef} tabIndex={-1} className="aur-bma-title">Der AimUp Board Manager ist da</DialogTitle>
          <DialogDescription className="aur-bma-text">
            Unser Board Manager für Windows ist jetzt im Microsoft Store verfügbar.
            Verbinde deine Kameras von deinem Autoscoring-System mit dem PC.
          </DialogDescription>
        </DialogHeader>

        <ul className="aur-bma-list">
          <li><Camera size={18} aria-hidden="true" /><span>Drei-Kamera-System</span></li>
          <li><Target size={18} aria-hidden="true" /><span>Automatisches Scoring</span></li>
        </ul>

        <div className="aur-bma-actions">
          <button type="button" onClick={goToDownload} className="aur-bma-primary">
            <Download size={18} aria-hidden="true" />
            Board Manager entdecken & herunterladen
          </button>
          <button type="button" onClick={dismiss} className="aur-bma-later">
            Später
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
